import { formatSecondsAsTimer } from "@vericus/cadmus-common";
import { RecordCircleIcon } from "@vericus/cadmus-icons";
import { colors, Text } from "@vericus/cadmus-ui";

import type { UseWebcamCaptureReturn } from "@/components/webcam-capture";
import { useAppSelector } from "@/data/hooks";
import type { CountdownMessage } from "@/features/timeline/use-countdown/countdown-message";

import {
  selectResponseEndDate,
  selectResponseEndDatePassed,
  selectSocraticDialogueState,
} from "../../socratic-slice";
import * as styles from "./socratic-timers.css";

interface Props {
  webcam: UseWebcamCaptureReturn;
  preparationCountdown: CountdownMessage | null;
}

export function SocraticTimers(props: Props) {
  const { webcam, preparationCountdown } = props;

  const responseEndDate = useAppSelector(selectResponseEndDate);
  const responseEndDatePassed = useAppSelector(selectResponseEndDatePassed);

  const {
    isRecording,
    responseTimeSeconds: responseTimeLimit,
    recordingComplete,
  } = useAppSelector(selectSocraticDialogueState);

  const showPreparationCountdown = preparationCountdown !== null;

  const showRecordingTimer =
    isRecording && responseEndDatePassed === false && responseEndDate != null;

  return (
    <>
      {isRecording && <RecordingIndicator isRecording={isRecording} />}
      {showPreparationCountdown && (
        <PreparationCountdown message={preparationCountdown.message} />
      )}

      {showRecordingTimer && (
        <RecordingTimer
          responseDuration={webcam.recordingDuration}
          responseTimeLimit={responseTimeLimit ?? webcam.maxDurationSeconds}
        />
      )}

      {recordingComplete && <ReadyToSubmitIndicator />}
    </>
  );
}

function RecordingIndicator(props: { isRecording: boolean }) {
  if (!props.isRecording) {
    return null;
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <RecordCircleIcon
        size={24}
        color={colors.poppy500}
        label=""
        className={styles.recordingDot}
      />
      <Text kind="bodyLine">Recording now</Text>
    </div>
  );
}

function PreparationCountdown(props: { message: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <RecordCircleIcon size={24} color={colors.poppy500} label="" />
      <Text kind="bodyLine">Recording will start in {props.message}</Text>
    </div>
  );
}

function ReadyToSubmitIndicator() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <RecordCircleIcon size={24} color={colors.poppy500} label="" />
      <Text kind="bodyLine">Ready to submit</Text>
    </div>
  );
}

function RecordingTimer(props: {
  responseDuration: number;
  responseTimeLimit: number;
}) {
  return (
    <div style={{ display: "flex" }}>
      <Text kind="headingTwo">
        {formatSecondsAsTimer(props.responseDuration)}
      </Text>
      <Text kind="headingTwo" color="grey300">
        /{formatSecondsAsTimer(props.responseTimeLimit)}
      </Text>
    </div>
  );
}
